import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, App, Button, Modal, Segmented, Tag } from 'antd';

import DialogLoadState from '../common/DialogLoadState';
import GroupHeader from '../common/GroupHeader';
import PausedRulesNotice from '../discover/PausedRulesNotice';
import { confirmDestructive } from '../common/confirmDestructive';
import DrivenBackfillWarning from '../cards/DrivenBackfillWarning';
import {
	acceptAllNote, getBundleUpdate, ignoreBundleUpdate, notOfferedWords, pausedRulesOf, recomputeLine, recomputeWaiting, takeBundleUpdate, takenByAcceptAll, updateGroups, updateTakenNote,
} from '../../services/packages';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiErrors';
import { EXTERNAL_LINK_PROPS } from '../common/ExternalLink';

/**
 * "Review update" for one installed bundle (keepr-api KPR-458; epic KPE-015,
 * the owner's Q3: "propose changes to the user and let them cherry pick what
 * to take and what to leave"). Opened from the bundle's group in the Card
 * Library (KPR-1130) and from a row of the bundle's page's *In your
 * collections* (KPR-364); managers only.
 *
 * The dialog is drawn from the server's proposal (nothing is written to read
 * it): each change of the published version, as one row, grouped (cards and
 * their elements; views, launchers and charts; rules and reminders), with a
 * three-way choice — Take, Keep mine, or Later (left for another time). What
 * the person changed here too is a CONFLICT: the row says so and shows both
 * values, and starts at Later, as a change that recomputes stored values does.
 * A change that cannot be taken here says why and has no choice. A change kept
 * earlier sits under *Kept as yours*, where it can still be taken.
 *
 * A repeating group's changes (KPR-696) are rows of their card, each naming
 * the group and the column.
 *
 * A removal that hides values people recorded, or keeps a card that holds
 * records (`holdsData`, KPR-1421), starts at Later too and Accept all leaves
 * it: its row says how many records are involved and that nothing recorded is
 * deleted, and Take on its row works as ever.
 *
 * A change that works stored values out again (`recomputes`: a calculation
 * worked out another way) starts at Later, and Accept all never takes it.
 * Choosing Take on its row asks first, one change at a time (KPR-1057, the
 * owner's ruling): an element's shows the card editor's own confirmation
 * content (DrivenBackfillWarning: how many records hold a value, a few of them
 * now and after, a door to all of them), a group column's names the records
 * it reaches; **Take it** sets the row to Take, **Cancel** leaves it at Later
 * and nothing is sent. The row itself says how many records hold a value. One
 * that reads something the same update adds waits for it: its row names that
 * change, and cannot be set to Take until the change is in. A typed element
 * the update would make calculated over values cannot be taken (the server's
 * sentence), with a door to the card, where the card editor makes it.
 *
 * The footer: **Apply** (the one filled button) sends the rows' choices;
 * **Accept all** takes every clean change and leaves conflicts to be chosen
 * (said beside it, before it is pressed); **Ignore this update** turns
 * *Update available* off until a newer version. After a take the dialog says
 * what landed, what could not and why, and what is still to choose. A
 * proposal that changed meanwhile (`update_preview_stale`) is read again and
 * said so; a failure part-way shows what was taken and reads it again.
 *
 * No query, id or token is ever shown: the server sends words (`summary`) and
 * values a person can read (`yours`, `theirs`), or none.
 */

const CHOICES = [
	{ label: 'Take', value: 'take' },
	{ label: 'Keep mine', value: 'keep' },
	{ label: 'Later', value: 'later' },
];

/** The choice a row starts at: a clean change Take; a conflict, a recompute, an opening rule or a removal that hides recorded values Later; one kept earlier Later. */
export function initialChoice(change) {
	if (change.applicable === false) return null;
	if (change.state === 'declined') return 'later';
	return takenByAcceptAll(change) ? 'take' : 'later';
}

/** The decisions the rows hold, as the API takes them: `{ accept, decline }`. */
export function decisionsOf(changes, choices) {
	const accept = [];
	const decline = [];
	(changes || []).forEach((c) => {
		const choice = choices[c.id];
		if (choice === 'take' && c.applicable !== false) accept.push(c.id);
		else if (choice === 'keep' && c.state !== 'declined') decline.push(c.id);
	});
	return { accept, decline };
}

/** The summaries of the changes a waiting recompute reads, as the person sees them. */
function waitingOn(change, changesById) {
	return (change.needs || []).map(id => changesById.get(id)).filter(Boolean);
}

function ChangeRow({ change, choice, onChoose, disabled, changesById }) {
	const both = change.op === 'change' && (change.yours || change.theirs);
	const waiting = recomputeWaiting(change);
	const line = recomputeLine(change);
	const needed = waiting ? waitingOn(change, changesById) : [];
	const cardId = change.target?.card?.id;
	const opensCard = change.applicable === false && cardId && (change.code === 'typed_to_calculated' || change.code === 'preview_refused');
	const options = waiting ? CHOICES.map(o => (o.value === 'take' ? { ...o, disabled: true } : o)) : CHOICES;
	return (
		<li className={`review-update__row${change.conflict ? ' review-update__row--conflict' : ''}`} data-testid='review-update-row' data-change={change.id}>
			<div className='review-update__text'>
				<p className='review-update__summary'>{change.summary}</p>
				<div className='review-update__marks'>
					{change.conflict ? <Tag className='review-update__mark review-update__mark--conflict'>{change.basis === 'unknown' ? 'Cannot tell' : 'You changed this'}</Tag> : null}
					{change.recomputes ? <Tag className='review-update__mark'>Works out every value again</Tag> : null}
					{change.paused ? <Tag className='review-update__mark'>Arrives paused</Tag> : null}
					{change.loosens ? <Tag className='review-update__mark'>Opens it to more people</Tag> : null}
					{change.holdsData ? <Tag className='review-update__mark' data-testid='review-update-holds'>{change.kind === 'card' ? 'Holds records' : 'Hides recorded values'}</Tag> : null}
					{change.addOn ? <Tag className='review-update__mark'>{change.addOnName || change.addOn}</Tag> : null}
				</div>
				{both ? (
					<dl className='review-update__values' data-testid='review-update-values'>
						<div><dt>Yours</dt><dd>{change.yours || <span className='review-update__muted'>(none)</span>}</dd></div>
						<div><dt>The bundle’s</dt><dd>{change.theirs || <span className='review-update__muted'>(none)</span>}</dd></div>
					</dl>
				) : null}
				{change.conflict && change.conflictWords ? <p className='review-update__note'>{change.conflictWords}</p> : null}
				{change.holdsData ? <p className='review-update__note' data-testid='review-update-holds-note'>Accept all leaves this one for you to choose. Nothing recorded is deleted.</p> : null}
				{line ? <p className='review-update__note' data-testid='review-update-recompute-line'>{line}</p> : null}
				{waiting ? (
					<p className='review-update__note' data-testid='review-update-waiting'>
						Waits for {needed.length ? needed.map(n => `“${n.summary}”${n.state === 'declined' ? ' (kept as yours)' : ''}`).join(' and ') : 'another change of this update'}: take that first, then this one with its values shown.
					</p>
				) : null}
				{change.applicable === false ? <p className='review-update__note' data-testid='review-update-why'>Can’t be taken here. {change.why}</p> : null}
				{opensCard ? (
					<p className='review-update__note'>
						<Link to={`/cards/${cardId}?tab=elements`} {...EXTERNAL_LINK_PROPS}>Open “{change.target.card.name}” in the card editor</Link>
					</p>
				) : null}
			</div>
			{change.applicable === false ? null : (
				<Segmented
					className='review-update__choice'
					size='small'
					options={options}
					value={choice || 'later'}
					disabled={disabled}
					onChange={value => onChoose(change, value)}
					aria-label={`Your choice: ${change.summary}`}
				/>
			)}
		</li>
	);
}

function ResultNotice({ result, changesById, name }) {
	const failed = result.failed || [];
	// The person's own rules the update paused, and the warning when a re-judge
	// could not run (KPR-1354, KPR-1372). A partial answer is stored flat, so
	// its own `pausedRules` and `automationsWarning` are read the same way.
	const warned = typeof result.automationsWarning === 'string' ? result.automationsWarning.trim() : '';
	const paused = pausedRulesOf(result.pausedRules).total > 0;
	return (
		<Alert
			type={failed.length || result.partial || warned || paused ? 'warning' : 'success'}
			showIcon
			className='review-update__result'
			data-testid='review-update-result'
			message={result.partial ? 'The update stopped part-way. What it took stays taken.' : updateTakenNote(result, name)}
			description={failed.length || warned || paused ? (
				<>
					{failed.length ? (
						<ul>
							{failed.map(f => (
								<li key={f.id}>{(changesById.get(f.id) || {}).summary || 'A change'} {f.message}</li>
							))}
						</ul>
					) : null}
					<PausedRulesNotice pausedRules={result.pausedRules} />
					{warned ? <p data-testid="review-update-warning">{warned}</p> : null}
				</>
			) : null}
		/>
	);
}

export default function ReviewUpdateDialog({ bundleKey, bundleName, collectionId, onClose, onChanged }) {
	const { message, modal } = App.useApp();
	const [proposal, setProposal] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [again, setAgain] = useState(0);
	const [choices, setChoices] = useState({});
	const [busy, setBusy] = useState(false);
	const [note, setNote] = useState(null);
	const [result, setResult] = useState(null);
	const [showKept, setShowKept] = useState(false);

	useEffect(() => {
		let live = true;
		setLoading(true);
		setError(false);
		getBundleUpdate(collectionId, bundleKey)
			.then((data) => {
				if (!live) return;
				setProposal(data);
				setChoices(Object.fromEntries((data.changes || []).map(c => [c.id, initialChoice(c)])));
				setLoading(false);
			})
			.catch((err) => { if (live) { setError(apiErrorMessage(err, 'Unable to load this update.')); setLoading(false); } });
		return () => { live = false; };
	}, [collectionId, bundleKey, again]);

	const reload = useCallback(() => setAgain(n => n + 1), []);
	const name = proposal?.package?.name || bundleName || 'this bundle';
	const changes = useMemo(() => proposal?.changes || [], [proposal]);
	const changesById = useMemo(() => new Map(changes.map(c => [c.id, c])), [changes]);
	const open = changes.filter(c => c.state === 'proposed');
	const kept = changes.filter(c => c.state === 'declined');
	const { accept, decline } = decisionsOf(changes, choices);
	const setChoice = (id, value) => setChoices(prev => ({ ...prev, [id]: value }));
	/**
	 * A row's choice. Take on a recompute asks first, one at a time
	 * (KPR-1057): Take it sets the row, Cancel leaves it as it was.
	 */
	const choose = (change, value) => {
		if (value !== 'take' || !change.recomputes) { setChoice(change.id, value); return; }
		if (recomputeWaiting(change)) return;
		const r = change.recompute;
		if (change.kind === 'element' && r) {
			const label = change.target?.element?.label || r.element?.name || change.target?.element?.name;
			confirmDestructive(modal, {
				title: `Replace the values in “${label}”?`,
				width: 680,
				okText: 'Take it',
				cancelText: 'Cancel',
				content: (
					<div data-testid='review-update-recompute'>
						<DrivenBackfillWarning
							mode='update'
							previews={[ { element: { ...(r.element || {}), name: r.element?.name || change.target?.element?.name, label: { singular: label } }, preview: r } ]}
							collectionId={collectionId}
							cardId={change.target?.card?.id}
						/>
					</div>
				),
				onOk: () => setChoice(change.id, 'take'),
			});
			return;
		}
		confirmDestructive(modal, {
			title: 'Work this calculation out again?',
			okText: 'Take it',
			cancelText: 'Cancel',
			content: (
				<div data-testid='review-update-recompute'>
					<p>Every entry it reaches is worked out again, and what it holds now is replaced.</p>
					<p>{change.summary}</p>
				</div>
			),
			onOk: () => setChoice(change.id, 'take'),
		});
	};

	const afterTake = (answer, body) => {
		setResult(body?.acceptAll ? { ...answer, acceptAll: true } : answer);
		onChanged?.();
		reload();
	};
	const failedTake = (err) => {
		const code = apiErrorCode(err);
		const data = err?.response?.data || {};
		if (data.partial) {
			setResult({ ...data.partial, partial: true });
			onChanged?.();
			reload();
		} else if (code === 'update_preview_stale') {
			setNote('The update changed while this was open, so it was read again. Check your choices, then apply.');
			reload();
		} else {
			message.error(apiErrorMessage(err, 'The update could not be taken. Nothing was changed.'));
		}
	};
	const send = async (body) => {
		if (!proposal || busy) return;
		setBusy(true);
		setNote(null);
		try {
			afterTake(await takeBundleUpdate(collectionId, bundleKey, { previewToken: proposal.previewToken, ...body }), body);
		} catch (err) {
			failedTake(err);
		} finally {
			setBusy(false);
		}
	};
	const ignore = async () => {
		if (!proposal || busy) return;
		setBusy(true);
		try {
			await ignoreBundleUpdate(collectionId, bundleKey, proposal.publishedVersion);
			message.success(`Version ${proposal.publishedVersion} of ${name} is set aside. You’ll see a newer one when it comes.`);
			onChanged?.();
			onClose();
		} catch (err) {
			if (apiErrorCode(err) === 'update_version_changed') { setNote('A newer version was published while this was open, so it was read again.'); reload(); }
			else message.error(apiErrorMessage(err, 'The update could not be set aside.'));
		} finally {
			setBusy(false);
		}
	};

	/** Apply: the rows' choices (a recompute was confirmed when its row was set to Take). */
	const apply = () => send({ accept, decline });

	const upToDate = proposal?.upToDate === true;
	const nothingChosen = !accept.length && !decline.length;
	const canAcceptAll = open.some(takenByAcceptAll) || open.some(c => c.applicable === false);
	const footer = upToDate || !proposal ? [
		<Button key='close' onClick={onClose}>Close</Button>,
	] : [
		<Button key='ignore' className='review-update__ignore' onClick={ignore} disabled={busy || proposal.ignored}>Ignore this update</Button>,
		<Button key='all' onClick={() => send({ acceptAll: true })} disabled={busy || !canAcceptAll} data-testid='review-update-accept-all'>Accept all</Button>,
		<Button key='apply' type='primary' onClick={apply} loading={busy} disabled={nothingChosen} data-testid='review-update-apply'>
			{nothingChosen ? 'Apply' : `Apply ${accept.length + decline.length} ${accept.length + decline.length === 1 ? 'choice' : 'choices'}`}
		</Button>,
	];

	return (
		<Modal
			open
			width={760}
			title={`Update ${name}`}
			onCancel={busy ? undefined : onClose}
			footer={footer}
			destroyOnHidden
			className='review-update-modal'
		>
			<div className='review-update' data-testid='review-update'>
				<DialogLoadState
					loaded={Boolean(proposal)}
					loading={loading}
					error={Boolean(error)}
					errorDetail={typeof error === 'string' ? error : undefined}
					label='this update'
					onRetry={reload}
				>
					{proposal ? (
						<>
							{result ? <ResultNotice result={result} changesById={changesById} name={name} /> : null}
							{note ? <Alert type='info' showIcon message={note} className='review-update__note-banner' /> : null}
							{upToDate ? (
								<p className='review-update__intro'>This collection has the newest version of {name}.</p>
							) : (
								<>
									<p className='review-update__intro'>
										This collection has version {proposal.version} of {name}; version {proposal.publishedVersion} is published.
										Choose what to take. Nothing changes until you apply.
									</p>
									{proposal.basis !== 'known' ? (
										<Alert
											type='info'
											showIcon
											className='review-update__basis'
											message='keepr has no copy of the version this collection came from'
											description='So it cannot tell what you changed here. Each change shows your value beside the bundle’s; Accept all takes only what is new.'
										/>
									) : null}
									{proposal.ignored ? <p className='review-update__muted'>You set this version aside. You can still take any of it.</p> : null}
									{updateGroups(open).map(group => (
										<section key={group.key} className='review-update__group' aria-label={group.label}>
											<GroupHeader as='h4' label={group.label} count={group.changes.length} countLabel='change' />
											<ul className='review-update__list'>
												{group.changes.map(change => (
													<ChangeRow key={change.id} change={change} choice={choices[change.id]} onChoose={choose} disabled={busy} changesById={changesById} />
												))}
											</ul>
										</section>
									))}
									{!open.length ? <p className='review-update__intro'>Every change of this version is decided.</p> : null}
									{kept.length ? (
										<section className='review-update__group' aria-label='Kept as yours'>
											<GroupHeader
												as='h4'
												label='Kept as yours'
												count={kept.length}
												countLabel='change'
												collapsible
												collapsed={!showKept}
												onToggle={() => setShowKept(v => !v)}
											/>
											{showKept ? (
												<ul className='review-update__list'>
													{kept.map(change => (
														<ChangeRow key={change.id} change={change} choice={choices[change.id]} onChoose={choose} disabled={busy} changesById={changesById} />
													))}
												</ul>
											) : null}
										</section>
									) : null}
									{(proposal.notOffered || []).length ? (
										<p className='review-update__muted' data-testid='review-update-not-offered'>
											Not offered in an update yet: {(proposal.notOffered || []).map(notOfferedWords).join(', ')}.
										</p>
									) : null}
									{open.length ? <p className='review-update__muted' data-testid='review-update-all-note'>{acceptAllNote(changes)}</p> : null}
								</>
							)}
						</>
					) : null}
				</DialogLoadState>
			</div>
		</Modal>
	);
}
